import { Color as PersonaColor, Spacing } from '@persona-id/design-tokens';
import Color from 'color';
import { BORDER_RADIUSES, FONT_SIZES } from 'shared/styles/variables';
import { DefaultTheme } from 'styled-components';

import defaultTheme from 'design-system/theme';
import { InputStyles } from 'lib/interfaces/input-styles';
import {
  ButtonPosition,
  FontWeight,
  IconStyle,
  NavbarLogoDisplay,
  PageTransition,
  PictographPosition,
  ProcessingDisplayStyle,
  TermsTextPosition,
  TextAlign,
  TextDecorationLine,
  UsStateInputMethod,
  VerticalOptionsStyle,
} from 'lib/interfaces/ThemeSerialized';
import { Theme } from 'verify/models/theme';

export function serializeTheme(theme?: Theme): DefaultTheme {
  const attr = theme?.attributes ?? {};
  const colorPrimary = attr['color-primary'] ?? PersonaColor.CorePurple700;
  const iconColorPrimary =
    attr['icon-color-primary'] ?? Color(colorPrimary).mix(Color('white'), 0.4).hex();

  const defaultLineHeightBody = attr['font-size-body']
    ? `calc(${attr['font-size-body']} * 1.5);`
    : '';
  const defaultLineHeightHeader = attr['font-size-header']
    ? `calc(${attr['font-size-header']} * 1.25);`
    : '';
  const defaultLineHeightSmall = attr['font-size-small']
    ? `calc(${attr['font-size-small']} * 1.5);`
    : '';

  return {
    ...defaultTheme,
    // TODO: split this up, since buttons and inputs default differently
    borderRadius: attr['border-radius'] ?? BORDER_RADIUSES.rounded,
    borderRadiusModal: attr['border-radius-modal'] ?? '1px',
    borderWidth: attr['border-width'] ?? '2px',
    buttonBackgroundImage: attr['button-background-image'],
    buttonFontWeight: attr['button-font-weight'] ?? FontWeight.Medium,
    buttonPosition: attr['button-position'] ?? ButtonPosition.Inline,
    buttonShadowStrength: attr['button-shadow-strength'],
    buttonTextTransform: attr['button-text-transform'],
    colorButtonPrimary: attr['color-button-primary'] ?? colorPrimary,
    colorButtonSecondary: attr['color-button-secondary'] ?? colorPrimary,
    colorButtonSecondaryFill: attr['color-button-secondary-fill'],
    colorButtonPrimaryFillDisabled:
      attr['color-button-primary-fill-disabled'] ?? PersonaColor.CoreSlate300,
    colorButtonSecondaryFillDisabled:
      attr['color-button-secondary-fill-disabled'] ?? PersonaColor.CoreSlate300,
    colorError: attr['color-error'] ?? PersonaColor.CoreRed900,
    colorFont: attr['color-font'] ?? PersonaColor.CoreSlate1000,
    colorFontButtonPrimary: attr['color-font-button-primary'] ?? PersonaColor.CoreSlate50,
    colorFontButtonSecondary: attr['color-font-button-secondary'],
    colorFontSmall: attr['color-font-small'] ?? PersonaColor.CoreSlate1000,
    colorFontTitle: attr['color-font-title'] ?? PersonaColor.CoreSlate1000,
    colorIconHeader: attr['color-icon-header'] ?? PersonaColor.CoreSlate700,
    colorInputBackground:
      attr['color-input-background'] ?? attr['color-modal-background'] ?? PersonaColor.PrimaryWhite,
    colorInputBorder: attr['color-input-border'] ?? PersonaColor.CoreSlate500,
    colorFooterBackground: attr['color-modal-background'] ?? PersonaColor.CoreSlate50,
    colorLink: attr['color-link'] ?? colorPrimary,
    colorDropdownBackground: attr['color-dropdown-background'] ?? PersonaColor.PrimaryWhite,
    colorDropdownOption: attr['color-dropdown-option'] ?? PersonaColor.CoreSlate50,
    colorModalBackground: attr['color-modal-background'] ?? PersonaColor.PrimaryWhite,
    colorPrimary,
    colorProgressBar: attr['color-progress-bar'] ?? colorPrimary,
    colorSuccess: attr['color-success'] ?? PersonaColor.CoreGreen500,
    colorWarning: attr['color-warning'] ?? PersonaColor.CoreYellow900,
    colorDivider: attr['color-divider'] ?? PersonaColor.CoreSlate200,
    fontFamily: attr['font-family'] ?? defaultTheme.text.fontFamily.base,
    fontFamilyTitle: attr['font-family-title'] ?? defaultTheme.text.fontFamily.base,
    fontUrl: attr['font-url'],
    fontSizeBody: attr['font-size-body'] ?? FONT_SIZES.p,
    fontSizeHeader: attr['font-size-header'] ?? FONT_SIZES.h2,
    fontSizeSmall: attr['font-size-small'] ?? '10px',
    headerFontWeight: attr['header-font-weight'] ?? FontWeight.Medium,
    headerMarginBottom: attr['header-margin-bottom'] ?? Spacing[4],
    iconColorPrimary: iconColorPrimary,
    iconColorHighlight:
      attr['icon-color-highlight'] ?? Color(iconColorPrimary).mix(Color('white'), 0.85).hex(),
    iconColorStroke:
      attr['icon-color-stroke'] ??
      Color(colorPrimary).mix(Color('black'), 0.71).saturate(0.3).hex(),
    iconColorBackground: attr['icon-color-background'] ?? PersonaColor.PrimaryWhite,
    // @deprecated -- only for old OptionVertical icon fill. New OptionVertical design just uses colorPrimary.
    iconColorGovernmentIdType: attr['icon-color-government-id-type'] ?? colorPrimary,
    inputStyle: attr['input-style'] ?? InputStyles.Outlined,
    lineHeightBody: attr['line-height-body'] ?? defaultLineHeightBody,
    lineHeightHeader: attr['line-height-header'] ?? defaultLineHeightHeader,
    lineHeightSmall: attr['line-height-small'] ?? defaultLineHeightSmall,
    logoData: attr['logo-data'],
    logoFilename: attr['logo-filename'],
    logoHeight: attr['logo-height'],
    logoUrl: attr['logo-url'],
    logoWidth: attr['logo-width'],
    pageTransition: attr['page-transition'] ?? PageTransition.Slide,
    textAlign: attr['text-align'] ?? TextAlign.Default,
    textDecorationLineLink: attr['text-decoration-line-link'] ?? TextDecorationLine.Underline,
    usStateInputMethod: attr['us-state-input-method'] ?? UsStateInputMethod.Input,
    navbarLogoDisplay: attr['navbar-logo-display'] ?? NavbarLogoDisplay.None,
    navbarLogoHeight: attr['navbar-logo-height'],
    iconStyle: attr['icon-style'] ?? IconStyle.Default,
    processingDisplayStyle: attr['processing-display-style'] ?? ProcessingDisplayStyle.Default,
    verticalOptionsStyle: attr['vertical-options-style'] ?? VerticalOptionsStyle.Default,
    governmentIdPictographPosition:
      attr['government-id-pictograph-position'] ?? PictographPosition.Center,
    idBackPictographHeight: attr['id-back-pictograph-height'],
    idBackPictographUrl: attr['id-back-pictograph-url'],
    idFrontPictographHeight: attr['id-front-pictograph-height'],
    idFrontPictographUrl: attr['id-front-pictograph-url'],
    passportFrontPictographHeight: attr['passport-front-pictograph-height'],
    passportFrontPictographUrl: attr['passport-front-pictograph-url'],
    passportSignaturePictographHeight: attr['passport-signature-pictograph-height'],
    passportSignaturePictographUrl: attr['passport-signature-pictograph-url'],
    // No Persona asset; only show custom asset if present
    governmentIdSelectPictographHeight: attr['government-id-select-pictograph-height'] ?? 'none',
    governmentIdSelectPictographUrl: attr['government-id-select-pictograph-url'],
    deviceHandoffTermsTextPosition:
      attr['device-handoff-terms-text-position'] ?? TermsTextPosition.BelowButton,
    selfiePictographUrl: attr['selfie-pictograph-url'],
    selfiePictographHeight: attr['selfie-pictograph-height'],
    selfieCenterPictographUrl: attr['selfie-center-pictograph-url'],
    selfieCenterPictographHeight: attr['selfie-center-pictograph-height'],
    selfieLeftPictographUrl: attr['selfie-left-pictograph-url'],
    selfieLeftPictographHeight: attr['selfie-left-pictograph-height'],
    selfieRightPictographUrl: attr['selfie-right-pictograph-url'],
    selfieRightPictographHeight: attr['selfie-right-pictograph-height'],
    documentPictographHeight: attr['document-pictograph-height'],
    documentPictographPosition: attr['document-pictograph-position'] ?? PictographPosition.Center,
    documentPictographUrl: attr['document-pictograph-url'],
    failedPictographHeight: attr['failed-pictograph-height'],
    failedPictographPosition: attr['failed-pictograph-position'] ?? PictographPosition.Center,
    failedPictographUrl: attr['failed-pictograph-url'],
    successPictographHeight: attr['success-pictograph-height'],
    successPictographPosition: attr['success-pictograph-position'] ?? PictographPosition.Center,
    successPictographUrl: attr['success-pictograph-url'],
    exitPictographHeight: attr['exit-pictograph-height'],
    exitPictographPosition: attr['exit-pictograph-position'] ?? PictographPosition.Center,
    exitPictographUrl: attr['exit-pictograph-url'],
    loadingPictographHeight: attr['loading-pictograph-height'],
    loadingPictographUrl: attr['loading-pictograph-url'],
    cameraSupportPictographHeight: attr['camera-support-pictograph-height'],
    cameraSupportPictographUrl: attr['camera-support-pictograph-url'],
  };
}

export const DEFAULT_THEME = serializeTheme();
